import { useMemo } from 'react';
import { FilterValue, SortingRule } from 'react-table';

import { entityDefinition } from '@work4all/models/lib/Classes/entityDefinitions';
import { Filters } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';

import {
  assertNever,
  queryEquals,
  queryIn,
  queryOr,
  throwInDev,
} from '@work4all/utils';

import { READ_PRIORITY_BY_NORMAL_PRIORITY } from '../../../components/entity-picker/task-prio-picker/TaskPrioPicker';
import { FilterType } from '../types';
import { splitBusinessPartnersByType } from '../utils/filters/businessPartnerFilterSplit';
import {
  booleanNumberToQuery,
  booleanToQuery,
  dateToQuery,
  numbersToQuery,
  pickerToQuery,
  searchToQuery,
} from '../utils/filters/filterConversion';

export interface FilterSortState {
  filter: Filters | undefined;
  sort: SortingRule<never>[] | undefined;
}

export type CustomFilterMapping = Partial<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Record<FilterType, (id: string, values: any) => [string, any][]>
>;

type Filter = Record<string, unknown>;

export const usePrepareFilters = (
  filters?: any,
  sortBy?: any,
  customFilterMapping?: CustomFilterMapping,
  entity?: Entities
): FilterSortState => {
  const state: FilterSortState = useMemo(
    () => prepareFilters(filters, sortBy, customFilterMapping, entity),
    [sortBy, filters, customFilterMapping, entity]
  );

  return state;
};

export const prepareFilters = (
  filters?: any,
  sortBy?: any,
  customFilterMapping?: CustomFilterMapping,
  entity?: Entities
) => {
  const sort = sortBy ? sortBy : undefined;
  let filter: Filters | undefined = undefined;
  if (filters) {
    filter = filters.length
      ? filters.reduce((acc: Filter[], curr) => {
          const filter: FilterValue & { filterType: FilterType } = curr.value;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let query: any;
          const filterType: FilterType = filter.filterType;

          if (customFilterMapping && customFilterMapping[filterType]) {
            const mappings = customFilterMapping[filterType](
              curr.id,
              filter.value
            );
            mappings.forEach(([newId, newQuery]) => {
              acc.push({ [newId]: newQuery });
            });
            return acc;
          }

          switch (filterType) {
            case FilterType.ClosedStatus:
              if (filter.value) {
                let filters: Filter;

                if (
                  entity === Entities.project ||
                  curr.id === 'projectStatus.closedStatus'
                ) {
                  /**
                   * A workaround because projects have different properties
                   * to determine if it's closed or not
                   */
                  filters = {
                    $or: [
                      {
                        'projectStatus.closedStatus': { $eq: 0 },
                      },
                      {
                        'projectStatus.closedStatus': { $eq: null },
                      },
                    ],
                  };
                } else {
                  filters = { [curr.id]: { $eq: 0 } };
                }

                acc.push(filters);
                break;
              }
              break;
            case FilterType.Search:
              query = searchToQuery(filter.value);
              break;
            case FilterType.Date:
              query = dateToQuery(filter.value.startDate, filter.value.endDate);
              break;
            case FilterType.RequiredTime:
            case FilterType.Number:
              query = numbersToQuery(filter.value.from, filter.value.to);
              break;
            case FilterType.Primitive: {
              const value = Array.isArray(filter.value)
                ? filter.value
                : [filter.value];
              query = queryIn(value.map((id) => id));
              break;
            }
            case FilterType.TaskPriority: {
              const values = Array.isArray(filter.value)
                ? filter.value
                : [filter.value];

              query = queryIn(
                values.flatMap((value) => [
                  value.id,
                  READ_PRIORITY_BY_NORMAL_PRIORITY[value.id],
                ])
              );

              break;
            }
            case FilterType.ObjectType:
            case FilterType.Picker:
            case FilterType.EmailKind:
            case FilterType.VacationKind:
            case FilterType.EInvoiceFormat:
            case FilterType.TaskStatus:
            case FilterType.SalesOpportunitiesGroupPicker:
            case FilterType.SalesOpportunitiesStatusPicker:
            case FilterType.PaymentStatus:
            case FilterType.InvoiceForm:
            case FilterType.ReAccountingStatus:
            case FilterType.RaAccountingStatus:
            case FilterType.PermitStatus:
            case FilterType.VacationStatus:
            case FilterType.InvoiceKind:
            case FilterType.LedgerAccountKind:
            case FilterType.DueDateClassified:
            case FilterType.AppointmentState:
            case FilterType.ArticleKind:
            case FilterType.RythmStatus:
            case FilterType.TicketPriority:
            case FilterType.ProjectTrafficLightStatus:
            case FilterType.TicketStatus: {
              const value = Array.isArray(filter.value)
                ? filter.value
                : [filter.value];
              // Ensure it's reference type by checking nesting
              if (curr.id.includes('.')) {
                // For BusinessPartner we need to specify BusinessPartnerType
                // BusinessPartner should be always reference type
                if (filterType === FilterType.Picker) {
                  const isBusinessPartnerField =
                    curr.id === 'businessPartnerId' ||
                    curr.id ===
                      'businessPartnerContactCombined.businessPartner.data.id';
                  const isIdField = curr.id.includes('id');

                  if (isBusinessPartnerField && isIdField) {
                    let typeField: string | undefined;

                    if (
                      entity &&
                      entityDefinition[entity]?.fieldDefinitions?.[
                        'businessPartnerType'
                      ]
                    ) {
                      typeField = 'businessPartnerType';
                    } else if (
                      entity &&
                      entityDefinition[entity]?.fieldDefinitions?.[
                        'businessPartner'
                      ]?.entity
                    ) {
                      typeField = 'businessPartner.businessPartnerType';
                    }

                    // entities which do not have businessPartnerType
                    if (
                      [
                        ...SKIP_BP_TYPE_CHECK,
                        Entities.salesOpportunities,
                      ].includes(entity)
                    ) {
                      typeField = null;
                    }

                    // Separate suppliers and customers with the appropriate type field if available
                    const splitFilter = splitBusinessPartnersByType(
                      value,
                      curr.id,
                      typeField
                    );
                    if (splitFilter) {
                      acc.push(splitFilter);
                      break;
                    }
                  }

                  // Ensure BusinessPartner entity - keep existing logic for backward compatibility
                  if (curr.id.includes('data')) {
                    const bpAcc = [];
                    let isBusinessPartnerFilter = false;

                    value.forEach((singleValue) => {
                      // if entity has no businessPartnerType property return
                      if (SKIP_BP_TYPE_CHECK.includes(entity)) return;
                      const bpTypefilterParam = 'businessPartnerType';

                      const propertyExist =
                        entityDefinition[entity]?.fieldDefinitions?.[
                          bpTypefilterParam
                        ];
                      if (!propertyExist) {
                        throwInDev(
                          `Entity '${entity}' has no '${bpTypefilterParam}' property.`
                        );
                        return;
                      }

                      let bpTypeFilterValue = undefined;

                      if (singleValue.__typename === 'Kunde') {
                        bpTypeFilterValue = SdObjType.KUNDE;
                      }
                      if (singleValue.__typename === 'Lieferant') {
                        bpTypeFilterValue = SdObjType.LIEFERANT;
                      }
                      if (bpTypeFilterValue) {
                        const singleBpAcc = [];
                        // OK, but then we need to make it together with id
                        singleBpAcc.push({
                          [bpTypefilterParam]: queryEquals(bpTypeFilterValue),
                        });

                        isBusinessPartnerFilter = true;

                        if (singleValue.id === '0') {
                          const keyBeforeData = curr.id.split('.data')[0];
                          const bpIdfilterParam = `${keyBeforeData}.data.id`;

                          singleBpAcc.push({
                            [bpIdfilterParam]: queryEquals(0),
                          });
                        } else {
                          singleBpAcc.push({
                            [curr.id]: queryEquals(singleValue.id),
                          });
                        }

                        bpAcc.push(queryOr(singleBpAcc));
                      }
                    });
                    // missing part about 0 & bp
                    if (bpAcc.length && !isBusinessPartnerFilter) {
                      acc.push(queryOr(bpAcc));
                      break;
                    }
                  }
                }

                // If we want to filter by reference we cannot use in [0] we need to use queryEquals null
                const smallAcc = handleIdZeroCase(value, curr);
                if (smallAcc) {
                  acc.push(queryOr(smallAcc));
                  break;
                }
              }

              query = pickerToQuery(value);
              break;
            }
            case FilterType.BooleanNumber:
              query = booleanNumberToQuery(
                Array.isArray(filter.value) ? filter.value : [filter.value]
              );
              break;
            case FilterType.Enum:
              query = queryIn(filter.value.map((x) => x.id));
              break;
            case FilterType.Boolean:
              query = booleanToQuery(
                Array.isArray(filter.value) ? filter.value : [filter.value]
              );
              break;
            case FilterType.AppointmentBillable:
              query = booleanNumberToQuery(
                Array.isArray(filter.value) ? filter.value : [filter.value]
              );
              break;
            case FilterType.Check:
              if (filter.value) {
                query = { $eq: 'true' };
              }
              break;
            case FilterType.TravelReceiptStatus: {
              let mappings = [];
              switch (filter.value) {
                case 'parked':
                  mappings = [['travelExpenses.closedByuserid', { $eq: 0 }]];
                  break;
                case 'closed':
                  mappings = [
                    ['travelExpenses.insertionREDate', { $eq: null }],
                    ['travelExpenses.closedByuserid', { $ne: 0 }],
                  ];
                  break;
                case 'booked':
                  mappings = [
                    ['travelExpenses.insertionREDate', { $ne: null }],
                    ['travelExpenses.closedByuserid', { $ne: 0 }],
                  ];
                  break;
              }
              mappings.forEach(([newId, newQuery]) => {
                acc.push({ [newId]: newQuery });
              });
              break;
            }
            case FilterType.TravelExpensesOpenStatus: {
              let mappings = [];
              switch (filter.value) {
                case 'open':
                  mappings = [
                    ['closedByuserid', { $eq: 0 }],
                    ['closedByuserid', { $eq: null }],
                  ];
                  break;
                case 'closed':
                  mappings = [['closedByuserid', { $ne: 0 }]];
                  break;
              }
              const newQuery = queryOr(
                mappings.map(([newId, newQuery]) => ({ [newId]: newQuery }))
              );
              acc.push(newQuery);
              break;
            }
            case FilterType.ChronoContactPicker:
              throw new Error(
                `${filterType} is not implemented as a generic filter.`
              );
            default:
              assertNever(filterType, `Unknown filter type ${filterType}`);
          }

          if (query) {
            acc.push({ [curr.id]: query });
          }

          return acc;
        }, [])
      : undefined;
  }
  return {
    sort,
    filter,
  };
};

const SKIP_BP_TYPE_CHECK = [Entities.travelReceipts];

function handleIdZeroCase(value, curr) {
  const ids = value.map((x) => x.id);
  const idZero = ids.find((x) => '0' === `${x}`);

  if (idZero) {
    const valueNoZero = value.filter((x) => '0' !== `${x.id}`);
    const smallAcc = [];

    // Special handling for PaymentKind: ID 0 is a valid entity, not null
    if (curr.id.includes('paymentKind.id')) {
      smallAcc.push({ [curr.id]: queryEquals(0) });
    } else {
      smallAcc.push({ [curr.id]: queryEquals(null) });
    }

    if (valueNoZero.length) {
      smallAcc.push({ [curr.id]: pickerToQuery(valueNoZero) });
    }

    return smallAcc;
  }

  return null;
}
